/**
 * THE NEW DESIGN'S ADDRESS — prest0.ai/prest0design/ (Miles, 2026-09-23).
 *
 * "when a user goes to prest0.ai/ (any tree) they get the same experience, but
 * replace prest0.ai/ with prest0.ai/prest0design/ (any tree) they get the same
 * exact web application but in the new design — when we approve it we move
 * everything to prest0.ai." (docs/PLAN-design-system-20260922.md §7a)
 *
 * ONE APP, ONE BUILD, TWO ADDRESSES. Both hosts already answer any path with
 * the same index.html (the GCS lane through the load balancer's fallback, the
 * Firebase lane through its `**` rewrite — measured 2026-09-23, both 200), so
 * the tree needs no serving change at all: when the address starts with
 * DESIGN_PREVIEW_BASE, src/index.tsx mounts the router with that prefix as its
 * basename, every <Link> and navigate() stays inside it, and the route table is
 * the live one. Only the surfaces already rebuilt render differently (App.tsx
 * swaps them in); every other route is today's page, working, under the prefix.
 *
 * THIS MODULE IS ON THE FIRST DOWNLOAD — keep it small. Everything the new
 * design draws lives in on-demand chunks (the `pavilion` row of
 * scripts/check-chat-first-paint.mjs), so a prest0.ai visitor pays for the
 * address check below and nothing else.
 *
 * The prefix rule mirrors react-router's own stripBasename, because the two
 * must agree about which addresses are in the tree: case-insensitive, and the
 * prefix must end at a "/" or at the end of the path — /prest0designer is not
 * in the tree, /PREST0DESIGN/pricing is.
 */

import { LIVE_ON_PREST0_AI, type DesignSurface } from './siteDesign.generated';

/** Every surface the new design has (the page registry keys its pages by these words). */
export type { DesignSurface };

export const DESIGN_PREVIEW_BASE = '/prest0design';

const currentPath = (): string =>
  typeof window !== 'undefined' && window.location ? window.location.pathname || '/' : '/';

/** The tree's prefix when `pathname` is inside it, else ''. */
export function designPreviewBase(pathname: string = currentPath()): string {
  const base = DESIGN_PREVIEW_BASE;
  if (!pathname.toLowerCase().startsWith(base)) return '';
  const next = pathname.charAt(base.length);
  return next === '' || next === '/' ? base : '';
}

/** True when `pathname` (default: the current address) is inside the new design's tree. */
export function isDesignPreview(pathname?: string): boolean {
  return designPreviewBase(pathname) !== '';
}

/**
 * WHERE THE NEW DESIGN SHOWS (Miles, 2026-09-24 late: "start implementing this entirely to prest0.ai
 * and keep prest0.ai/prest0design as we will use that location … for demonstration purposes").
 *
 * Two different questions, and every caller must ask the right one:
 *   - "is this the demonstration address?" — isDesignPreview(). The robots tag, the link keeper, the
 *     router's basename and the demonstration switches (`?chatwater=on`) answer to the ADDRESS.
 *   - "does this page show the new design's <surface>?" — designShows(surface). True everywhere in the
 *     tree (the demonstration shows every surface built), and on prest0.ai for the surfaces named in
 *     config/site-design.json's live list (its generated twin, ./siteDesign.generated.ts). The home page
 *     swap (App.tsx), the chat's skin and its part names answer to this.
 * A page never changes address without reloading, so the answer is fixed for the life of the page.
 */
export function designShows(
  surface: DesignSurface,
  pathname?: string,
  /** The live list (defaults to the committed one); injectable so tests drive both positions. */
  live: readonly string[] = LIVE_ON_PREST0_AI,
): boolean {
  return isDesignPreview(pathname) || live.includes(surface);
}

/**
 * The app's own path for an address, with the tree's prefix removed —
 * /prest0design/pricing → /pricing, /prest0design → /. Anything that reports
 * "which page is this" to the server (the guest chat's page context) must use
 * this, or the tree's pages would be strangers to it.
 */
export function appPathname(pathname: string = currentPath()): string {
  const base = designPreviewBase(pathname);
  if (!base) return pathname;
  const rest = pathname.slice(base.length);
  return rest === '' ? '/' : rest;
}

/**
 * A root-relative app path, kept in the tree the visitor is in. For the hard
 * navigations the router cannot see (window.location.replace after logout,
 * for one). Off the tree, and for anything that is not a root-relative path,
 * it returns the path unchanged.
 */
export function inCurrentTree(path: string, pathname: string = currentPath()): string {
  const base = designPreviewBase(pathname);
  if (!base || !path.startsWith('/') || path.startsWith('//')) return path;
  if (designPreviewBase(path.split(/[?#]/)[0])) return path;
  return base + path;
}

/** A path whose last segment has an extension is a file (sitemap.xml, a pdf, an image), never a route. */
const FILE_PATH_RE = /\/[^/]+\.[a-z0-9]{1,8}$/i;
/** Served, never routed: Firebase's reserved handlers, the API, hashed assets, media, widgets, the /desktop CDN sheet. */
const NOT_A_ROUTE_RE = /^\/(?:__\/|api\/|static\/|images\/|fonts\/|team\/|widgets\/|desktop-embed\/|demo-video\/)/i;

/** Would a plain same-origin link to `pathname` open a page of the app? */
export function isAppRoutePath(pathname: string): boolean {
  return pathname.startsWith('/') && !FILE_PATH_RE.test(pathname) && !NOT_A_ROUTE_RE.test(pathname);
}

/**
 * THE LINK KEEPER. The router keeps every <Link> and navigate() inside the
 * tree by itself, but some surfaces render plain <a href="/…"> (43 of them on
 * 2026-09-23 — the invite page, onboarding, the chat's sign-in note…), and a
 * plain anchor's full page load would drop the prefix and land the visitor on
 * prest0.ai. One listener, installed only inside the tree, puts the prefix
 * back: a plain left click on a same-origin link to an app route that is not
 * already in the tree navigates to its twin in the tree instead — still a full
 * page load, exactly as the anchor would have done.
 *
 * It stands aside for everything that is not that case: a click something else
 * already handled (defaultPrevented — a router <Link>, a hash link's own
 * handler), a modified or non-primary click (the visitor asked for a new tab),
 * a link with a target or a download, another origin, a file, and a link
 * already inside the tree. Bubble phase on purpose, so it runs after React's
 * own handlers and can see what they prevented.
 */
export function installPreviewLinkKeeper(
  doc: Document = document,
  /** How to leave for the twin address. A full page load, exactly as the anchor would have done; injectable for tests. */
  go: (url: string) => void = (url) => window.location.assign(url),
): () => void {
  const base = designPreviewBase();
  if (!base) return () => undefined;
  const onClick = (e: MouseEvent) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const start = e.target as Element | null;
    const anchor = start && typeof start.closest === 'function'
      ? (start.closest('a[href]') as Element | null)
      : null;
    if (!anchor) return;
    const target = anchor.getAttribute('target');
    if ((target && target.toLowerCase() !== '_self') || anchor.hasAttribute('download')) return;
    const href = anchor.getAttribute('href');
    if (!href) return;
    let url: URL;
    try {
      url = new URL(href, window.location.href);
    } catch {
      return;
    }
    if (url.origin !== window.location.origin) return;
    if (designPreviewBase(url.pathname)) return;
    if (!isAppRoutePath(url.pathname)) return;
    e.preventDefault();
    go(`${base}${url.pathname}${url.search}${url.hash}`);
  };
  doc.addEventListener('click', onClick);
  return () => doc.removeEventListener('click', onClick);
}

/**
 * Called once from src/index.tsx before the first render.
 *
 * Wherever the new design's chat shows — the tree, and prest0.ai since the chat went live — it marks
 * <html data-design="pavilion">, which the chat's skin is scoped by (CHAT_SKIN_SCOPE in
 * ./pavilion/chat/pavilionChatCss.ts, and the home sheet's one rule for the chat's disclosure line);
 * nothing else in the app reads that mark. Inside the tree only
 * it also keeps the address out of search (a demonstration of prest0.ai, reachable by anyone with
 * the address and indexed by no one) and installs the link keeper. On prest0.ai with no live chat
 * it does nothing at all.
 */
export function startDesignPreview(doc: Document = document, live: readonly string[] = LIVE_ON_PREST0_AI): void {
  if (designShows('chat', undefined, live)) doc.documentElement.setAttribute('data-design', 'pavilion');
  if (!isDesignPreview()) return;
  if (!doc.querySelector('meta[name="robots"][data-design-preview]')) {
    const meta = doc.createElement('meta');
    meta.setAttribute('name', 'robots');
    meta.setAttribute('content', 'noindex, nofollow');
    meta.setAttribute('data-design-preview', '');
    doc.head.appendChild(meta);
  }
  installPreviewLinkKeeper(doc);
}
